/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Helper to validate change settings profile password
 *
 * @author Anton Panasenko
 * @version 1.3.1
 * @since 1.3.0
 */

import * as Yup from 'yup';
import { ValidationError } from 'yup';

import {
    ESettingsProfilePasswordError,
    ESettingsProfileValidationCodes,
    mapSettingsProfilePasswordFieldsToErrors,
} from '../settingsProfileConstants';
import { ISettingsProfileMetaPassword } from '../settingsProfileTypes';
import { ISettingsProfilePasswordValidateParams } from './SettingsProfilePasswordModal';

export const parseYupValidationErrors = (errors: ValidationError) => {
    return errors.inner.reduce((errorsObject: { [key: string]: string }, error: ValidationError) => {
        if (error.path) {
            errorsObject[mapSettingsProfilePasswordFieldsToErrors[error.path]] = error.message;
        }
        return errorsObject;
    }, {})
};

export const settingsProfilePasswordValidation = (validationParams: ISettingsProfileMetaPassword) => {
    const { alphabet, minLength, maxLength, requiredDigit, requiredLetter } = validationParams;
    const letterRegExp = /[a-z]/i;
    const digitRegExp = /[0-9]/;
    const legalLettersRegExp = new RegExp('^[' + alphabet + ']*$');
    const options = {
        context: { alphabet, requiredDigit, requiredLetter },
        abortEarly: false,
    };

    return (values: ISettingsProfilePasswordValidateParams): {[key in ESettingsProfilePasswordError]?: string} => {
        let result = {};
        try {
            Yup.object().shape({
                passwordCurrent: Yup.string()
                    .when(['passwordNew', 'passwordRepeat'], {
                        is: (passwordNew: string, passwordNewRepeat: string) => !!passwordNew || !!passwordNewRepeat,
                        then: Yup.string().required(ESettingsProfileValidationCodes.PASSWORD_CURRENT_REQUIRED),
                    }),
                passwordNew: Yup.string()
                    .when('passwordCurrent', {
                        is: (passwordCurrent: string) => !!passwordCurrent,
                        then: Yup.string()
                            .min(minLength, ESettingsProfileValidationCodes.PASSWORD_ILLEGAL_LENGTH)
                            .max(maxLength, ESettingsProfileValidationCodes.PASSWORD_ILLEGAL_LENGTH)
                            .when('$requiredLetter', {
                                is: true,
                                then: Yup.string()
                                    .matches(letterRegExp, ESettingsProfileValidationCodes.PASSWORD_REQUIRED_LETTER),
                            })
                            .when('$requiredDigit', {
                                is: true,
                                then: Yup.string()
                                    .matches(digitRegExp, ESettingsProfileValidationCodes.PASSWORD_REQUIRED_DIGIT),
                            })
                            .when('$alphabet', {
                                is: (value: string) => !!value,
                                then: Yup.string().matches(legalLettersRegExp,
                                        ESettingsProfileValidationCodes.PASSWORD_ILLEGAL_LETTERS),
                            }),
                    }),
                passwordNewRepeat: Yup.string()
                    .when('passwordNew', {
                        is: (passwordNew: string) => !!passwordNew,
                        then: Yup.string()
                            .oneOf([Yup.ref('passwordNew')],
                                ESettingsProfileValidationCodes.PASSWORDS_NOT_EQUALS),
                    }),
            }, [['passwordCurrent', 'passwordNew']]).validateSync(values, options);
        } catch (errors: any) {
            result = { ...parseYupValidationErrors(errors) };
        }
        return result;
    }
};
